/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from "@/assets/images/logo.png";
import kendraPortrait from "@/assets/images/kendra-portrait.jpg";
import wellGoodLogo from "@/assets/images/well-good-logo.png";
import escapeLogo from "@/assets/images/escape-logo.png";
import viceLogo from "@/assets/images/vice-logo.png";
import huffpostLogo from "@/assets/images/huffpost-logo.png";
import parkedLogo from "@/assets/images/parked-logo.png";
import therapyEnv1 from "@/assets/images/therapy-environment-1.jpg";
import therapyEnv2 from "@/assets/images/therapy-environment-2.jpg";
import therapyEnv3 from "@/assets/images/therapy-environment-3.jpg";
import kintsugiSilhouettes from "@/assets/images/kintsugi-silhouettes.jpg";
import contactHero from "@/assets/images/contact-hero.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo mappings - original crawl path and clean component paths
  "/wp-content/uploads/2024/07/cropped-logo1-1.png": logo,
  "/images/logo.png": logo,
  "/assets/images/logo.png": logo,
  
  // Kendra portrait - hero section
  "/wp-content/uploads/2024/07/KendraPortraits-Revised04-scaled-1-683x1024.jpg": kendraPortrait,
  "/images/kendra-portrait.jpg": kendraPortrait,
  "/assets/images/kendra-portrait.jpg": kendraPortrait,
  
  // Media publication logos - As seen in section
  "/wp-content/uploads/2024/07/well-good-logo-resizes-website-assets-og-hero-768x403.png": wellGoodLogo,
  "/images/well-good-logo.png": wellGoodLogo,
  "/assets/images/well-good-logo.png": wellGoodLogo,
  
  "/wp-content/uploads/2024/07/5847e9aacef1014c0b5e4828-768x300.png": escapeLogo,
  "/images/escape-logo.png": escapeLogo,
  "/assets/images/escape-logo.png": escapeLogo,
  
  "/wp-content/uploads/2024/07/c5f16ebe-1dfb-463f-afe2-605c2104b0c4-768x427.png": viceLogo,
  "/images/vice-logo.png": viceLogo,
  "/assets/images/vice-logo.png": viceLogo,
  
  "/wp-content/uploads/2024/07/e67d401a-739a-43f6-aeb5-186b0b665a6e.png": huffpostLogo,
  "/images/huffpost-logo.png": huffpostLogo,
  "/assets/images/huffpost-logo.png": huffpostLogo,
  
  "/wp-content/uploads/2024/07/7bd47edc-4f01-4053-b27c-c324c45ae5ce-768x256.png": parkedLogo,
  "/images/parked-logo.png": parkedLogo,
  "/assets/images/parked-logo.png": parkedLogo,
  
  // Kintsugi silhouettes image - Sanctuary section
  "/wp-content/uploads/2024/07/23-1024x512.jpg": kintsugiSilhouettes,
  "/images/kintsugi-silhouettes.jpg": kintsugiSilhouettes,
  "/assets/images/kintsugi-silhouettes.jpg": kintsugiSilhouettes,
  
  // Therapy environment images - Services section
  "/images/therapy-environment-1.jpg": therapyEnv1,
  "/assets/images/therapy-environment-1.jpg": therapyEnv1,
  
  "/wp-content/uploads/2024/07/wq2.jpg": therapyEnv2,
  "/images/therapy-environment-2.jpg": therapyEnv2,
  "/assets/images/therapy-environment-2.jpg": therapyEnv2,
  
  "/wp-content/uploads/2024/07/aq8.jpg": therapyEnv3,
  "/images/therapy-environment-3.jpg": therapyEnv3,
  "/assets/images/therapy-environment-3.jpg": therapyEnv3,
  
  // Contact page hero image
  "/wp-content/uploads/2021/05/Contact-parallax-img.jpg": contactHero,
  "/images/contact-hero.jpg": contactHero,
  "/assets/images/contact-hero.jpg": contactHero,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
